/**
 * Collapsible section wrapper used by the dock-mission and docking-station
 * panels. The section header is the click target (chevron + title + sub),
 * with an optional `action` slot on the right whose clicks do not toggle
 * the section. Keeps demo panels scrollable but compact.
 */

import { useState, type ReactNode } from 'react';

interface Props {
  title: string;
  /** Subtitle / pill rendered next to the title (e.g. "3 pts"). */
  sub?: ReactNode;
  /** Element rendered on the right of the header — clicks here do not toggle. */
  action?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

export default function Collapsible({
  title,
  sub,
  action,
  defaultOpen = false,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="dock-demo-section">
      <div className="dock-demo-section__head">
        <button
          type="button"
          className="dock-demo-section__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span
            className={`dock-demo-section__chevron${open ? ' is-open' : ''}`}
            aria-hidden="true"
          >
            ▾
          </span>
          <h3>{title}</h3>
          {sub != null && <span className="dock-demo-section__sub">{sub}</span>}
        </button>
        {action != null && (
          <div
            className="dock-demo-section__action"
            // Stop click propagation so action buttons don't accidentally
            // toggle the section open/closed.
            onClick={(e) => e.stopPropagation()}
          >
            {action}
          </div>
        )}
      </div>
      {open && <div className="dock-demo-section__body">{children}</div>}
    </section>
  );
}
